import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit2, Trash2, Download, X, Smile, Meh, Frown } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function MoodIcon({ value, className }) {
  if (value <= 2) return <Frown className={className} />;
  if (value === 3) return <Meh className={className} />;
  return <Smile className={className} />;
}

function getMoodColor(value) {
  if (value === 1) return 'text-red-500';
  if (value === 2) return 'text-orange-500';
  if (value === 3) return 'text-yellow-500';
  if (value === 4) return 'text-lime-500';
  return 'text-green-500';
}

function getMoodLabel(value) {
  if (value === 1) return 'Difficult';
  if (value === 2) return 'Low';
  if (value === 3) return 'Okay';
  if (value === 4) return 'Good';
  return 'Great';
}

function MoodButton({ moodVal, selectedValue, onChange }) {
  const isSelected = selectedValue === moodVal;
  const color = getMoodColor(moodVal);
  
  return (
    <button
      type="button"
      onClick={() => onChange(moodVal)}
      className={`p-2 rounded-full transition-all ${isSelected ? `bg-secondary ${color} scale-110` : 'text-muted-foreground hover:bg-secondary/50'}`}
      title={getMoodLabel(moodVal)}
      data-testid={`mood-${moodVal}`}
    >
      <MoodIcon value={moodVal} className="h-6 w-6" />
    </button>
  );
}

function MoodSelector({ value, onChange }) {
  return (
    <div className="flex gap-2 justify-center">
      <MoodButton moodVal={1} selectedValue={value} onChange={onChange} />
      <MoodButton moodVal={2} selectedValue={value} onChange={onChange} />
      <MoodButton moodVal={3} selectedValue={value} onChange={onChange} />
      <MoodButton moodVal={4} selectedValue={value} onChange={onChange} />
      <MoodButton moodVal={5} selectedValue={value} onChange={onChange} />
    </div>
  );
}

function EntryCard({ entry, onEdit, onDelete }) {
  const moodColor = entry.mood ? getMoodColor(entry.mood) : 'text-muted-foreground';
  
  return (
    <div className="card-soft" data-testid={`journal-entry-${entry.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-foreground">{entry.title}</h3>
            {entry.mood && <MoodIcon value={entry.mood} className={`h-4 w-4 ${moodColor}`} />}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
        <div className="flex gap-1">
          <button onClick={() => onEdit(entry)} className="p-2 text-muted-foreground hover:text-foreground" data-testid="edit-entry-btn">
            <Edit2 className="h-4 w-4" />
          </button>
          <button onClick={() => onDelete(entry.id)} className="p-2 text-muted-foreground hover:text-destructive" data-testid="delete-entry-btn">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{entry.content}</p>
      {entry.tags && entry.tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {entry.tags.map((tag, i) => (
            <span key={i} className="text-xs bg-secondary px-2 py-0.5 rounded-full">{tag}</span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Journal() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [mood, setMood] = useState(null);
  const [tags, setTags] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/journal`, { headers: { Authorization: `Bearer ${token}` } });
      setEntries(res.data);
    } catch (err) {
      toast.error('Failed to load journal');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleSave = async () => {
    if (!content.trim()) {
      toast.error('Please write something');
      return;
    }
    setSaving(true);
    try {
      const data = { 
        title: title || undefined, 
        content, 
        mood, 
        tags: tags.split(',').map(t => t.trim()).filter(Boolean) 
      };
      
      if (editingEntry) {
        await axios.put(`${API}/journal/${editingEntry.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Entry updated');
      } else {
        await axios.post(`${API}/journal`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Entry saved');
      }
      
      resetForm();
      fetchEntries();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setTitle(entry.title || '');
    setContent(entry.content);
    setMood(entry.mood);
    setTags((entry.tags || []).join(', '));
    setShowEditor(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API}/journal/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Entry deleted');
      fetchEntries();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/journal/export`, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.data.entries;
      
      let text = "# My Journal\n\n";
      data.forEach(entry => {
        text += `## ${entry.title} (${entry.date})\n`;
        if (entry.mood) text += `Mood: ${entry.mood}/5\n`;
        text += `\n${entry.content}\n\n`;
        if (entry.tags.length) text += `Tags: ${entry.tags.join(', ')}\n`;
        text += "---\n\n";
      });
      
      const blob = new Blob([text], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'journal-export.md';
      a.click();
      toast.success(`Exported ${res.data.count} entries`);
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const resetForm = () => {
    setShowEditor(false);
    setEditingEntry(null);
    setTitle('');
    setContent('');
    setMood(null);
    setTags('');
  };

  return (
    <div className="min-h-screen pb-safe" data-testid="journal-page">
      <div className="max-w-lg mx-auto px-6 pt-8">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" /><span>Back</span>
          </button>
          <Button variant="ghost" size="sm" onClick={handleExport} data-testid="export-btn">
            <Download className="h-4 w-4 mr-1" />Export
          </Button>
        </div>

        <div className="mt-6 page-enter">
          <h1 className="font-heading text-3xl font-semibold text-foreground">Journal</h1>
          <p className="mt-2 text-muted-foreground">Reflect on your experiences</p>
        </div>

        {!showEditor && (
          <Button onClick={() => setShowEditor(true)} className="mt-6 w-full btn-primary" data-testid="new-entry-btn">
            <Plus className="h-5 w-5 mr-2" />New Entry
          </Button>
        )}

        {showEditor && (
          <div className="mt-6 card-soft page-enter" data-testid="entry-editor">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-foreground">{editingEntry ? 'Edit Entry' : 'New Entry'}</h2>
              <button onClick={resetForm} className="text-muted-foreground hover:text-foreground" data-testid="close-editor-btn">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <Input
              placeholder="Title (optional)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mb-3"
              data-testid="entry-title-input"
            />
            
            <Textarea
              placeholder="What's on your mind?"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="min-h-[150px] mb-4"
              data-testid="entry-content-input"
            />
            
            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-2 text-center">How are you feeling?</p>
              <MoodSelector value={mood} onChange={setMood} />
            </div>
            
            <Input
              placeholder="Tags (comma separated)"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              className="mb-4"
              data-testid="entry-tags-input"
            />
            
            <Button onClick={handleSave} disabled={saving} className="w-full btn-primary" data-testid="save-entry-btn">
              {saving ? 'Saving...' : 'Save Entry'}
            </Button>
          </div>
        )}

        <div className="mt-6 space-y-3" data-testid="entries-list">
          {loading ? (
            <p className="text-center text-muted-foreground py-8">Loading...</p>
          ) : entries.length === 0 ? (
            <div className="card-soft text-center py-8">
              <p className="text-muted-foreground">No journal entries yet</p>
              <p className="text-sm text-muted-foreground mt-1">Start writing to track your journey</p>
            </div>
          ) : (
            entries.map(entry => (
              <EntryCard key={entry.id} entry={entry} onEdit={handleEdit} onDelete={handleDelete} />
            ))
          )}
        </div>

        <div className="h-8" />
      </div>
    </div>
  );
}
